import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Divider,
  Form,
  Modal,
  Radio,
  Tag,
  Tooltip,
  message,
} from 'antd';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as DeleteIcon } from '../../../../../assets/svg/trash-solid.svg';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/pen-to-square-solid.svg';
import {
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  STATUS,
  TOY_RESERVATION_RULE_TYPES,
} from '../../../../../common/constants';
import { formatDate, hasPermission } from '../../../../../common/utils';
import CommonTable from '../../../../../components/CommonTable';
import SearchComponent from '../../../../../components/SearchComponent';
import {
  CREATE_TOY_RESERVATION_RULE,
  DELETE_TOY_RESERVATION_RULE,
  UPDATE_TOY_RESERVATION_RULE,
} from '../../graphql/Mutations';
import { TOY_RESERVATION_RULES } from '../../graphql/Queries';
import { buildRuleConfig, getRuleTypeLabel } from '../toyReservationRuleUtils';
import ConfigReservationRuleModal from './ConfigReservationRuleModal';
import ReservationRuleDetailsPopover from '../../../../../components/ReservationRuleDetailsPopover';

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const TYPE_FILTER_OPTIONS = Object.values(TOY_RESERVATION_RULE_TYPES)?.map(
  (item) => ({ label: item?.label, value: item?.value }),
);

const STATUS_FILTER_OPTIONS = [
  { label: STATUS?.ACTIVE?.label, value: true },
  { label: STATUS?.INACTIVE?.label, value: false },
];

const ConfigReservationRuleTable = ({
  isRuleModalOpen,
  setIsRuleModalOpen,
  currentRule,
  setCurrentRule,
}) => {
  const {
    state: { permissions },
  } = useContext(AppContext);

  const [form] = Form.useForm();

  const [ruleList, setRuleList] = useState([]);
  const [ruleSearchTerm, setRuleSearchTerm] = useState('');
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isRuleLoading, setIsRuleLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [whereFilter, setWhereFilter] = useState({});
  const [appliedFilter, setAppliedFilter] = useState({});

  const [isDeletePrompt, setIsDeletePrompt] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  const hasCreatePermission = hasPermission(
    PERMISSIONS_KEY?.CONFIGS,
    PERMISSION_TYPE?.CREATE,
    permissions,
  );

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.CONFIGS,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  const hasDeletePermission = hasPermission(
    PERMISSIONS_KEY?.CONFIGS,
    PERMISSION_TYPE?.DELETE,
    permissions,
  );

  const [createRule] = useMutation(CREATE_TOY_RESERVATION_RULE, {
    onError: () => {},
  });

  const [updateRule] = useMutation(UPDATE_TOY_RESERVATION_RULE, {
    onError: () => {},
  });

  const [deleteRule] = useMutation(DELETE_TOY_RESERVATION_RULE, {
    onError: () => {},
  });

  const [fetchRules] = useLazyQuery(TOY_RESERVATION_RULES, {
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      setRuleList(response?.toyReservationRules?.data || []);
      setPaginationProp((prev) => ({
        ...prev,
        total: response?.toyReservationRules?.count ?? 0,
      }));
      setIsRuleLoading(false);
    },
    onError: () => {
      setRuleList([]);
      setIsRuleLoading(false);
    },
  });

  const fetchReservationRules = ({
    skip = (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
    limit = paginationProp?.pageSize || LIMIT,
    search = ruleSearchTerm,
    filters = appliedFilter,
  } = {}) => {
    setIsRuleLoading(true);
    const trimSearch = search?.trim();

    fetchRules({
      variables: {
        filter: {
          skip,
          limit,
          search: trimSearch || null,
          type: filters?.type || null,
          isActive:
            filters?.status === true || filters?.status === false
              ? filters?.status
              : null,
        },
      },
    });
  };

  useEffect(() => {
    fetchReservationRules({ skip: 0, limit: paginationProp?.pageSize || LIMIT });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (pagination, _tableFilter, _sorter, extra) => {
    if (extra?.action && extra?.action !== 'paginate') return;

    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);

    setPaginationProp((prev) => ({
      ...prev,
      ...pagination,
      current,
      pageSize: pagination?.pageSize || prev?.pageSize,
    }));

    fetchReservationRules({ skip, limit: pagination?.pageSize || LIMIT });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setRuleSearchTerm(trimValue);
    setPaginationProp((prev) => ({ ...prev, current: 1 }));
    fetchReservationRules({ skip: 0, search: trimValue });
  };

  const applyFilters = (filters) => {
    setWhereFilter(filters);
    setAppliedFilter(filters);
    setPaginationProp((prev) => ({ ...prev, current: 1 }));
    fetchReservationRules({ skip: 0, filters });
  };

  const handleSubmitRule = async (values) => {
    try {
      setIsActionLoading(true);

      const payload = {
        name: values?.name?.trim(),
        type: values?.type,
        config: buildRuleConfig(values?.type, values),
      };

      if (currentRule?.id) {
        const response = await updateRule({
          variables: {
            where: { id: currentRule?.id },
            data: { ...payload, isActive: !!values?.isActive },
          },
        });
        if (!response?.data?.updateToyReservationRule?.id) {
          throw new Error('Unable to update rule');
        }
        message?.success('Reservation rule updated successfully');
      } else {
        const response = await createRule({
          variables: { data: { ...payload, isActive: !!values?.isActive } },
        });
        if (!response?.data?.createToyReservationRule?.id) {
          throw new Error('Unable to create rule');
        }
        message?.success('Reservation rule created successfully');
      }

      setIsRuleModalOpen(false);
      setCurrentRule(null);
      setIsActionLoading(false);
      setPaginationProp((prev) => ({ ...prev, current: 1 }));
      fetchReservationRules({
        skip: 0,
        limit: paginationProp?.pageSize || LIMIT,
      });
    } catch (error) {
      setIsActionLoading(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!ruleToDelete?.id) return;
    try {
      setIsActionLoading(true);
      const response = await deleteRule({
        variables: { where: { id: ruleToDelete?.id } },
      });
      if (!response?.data?.deleteToyReservationRule) {
        throw new Error('Unable to delete rule');
      }
      setIsDeletePrompt(false);
      setRuleToDelete(null);
      setIsActionLoading(false);

      const isLastRowOnPage = ruleList?.length === 1 && paginationProp?.current > 1;
      const nextCurrent = isLastRowOnPage
        ? paginationProp?.current - 1
        : paginationProp?.current;
      setPaginationProp((prev) => ({ ...prev, current: nextCurrent }));
      fetchReservationRules({
        skip: (nextCurrent - 1) * (paginationProp?.pageSize || 0),
        limit: paginationProp?.pageSize || LIMIT,
      });
    } catch (error) {
      setIsActionLoading(false);
    }
  };

  const filterPopup = (dataIndex, options) => ({
    filterDropdown: ({ confirm, clearFilters }) => (
      <div className="custom-filter-dropdown">
        <Radio.Group
          className="d-flex flex-vertical"
          options={options}
          value={whereFilter?.[dataIndex]}
          onChange={(event) =>
            setWhereFilter((prev) => ({
              ...prev,
              [dataIndex]: event?.target?.value,
            }))
          }
        />
        <Divider className="divider-filter" />
        <div className="d-flex justify-center">
          <Button
            size="small"
            className="reset-button"
            onClick={() => {
              applyFilters({ ...appliedFilter, [dataIndex]: undefined });
              clearFilters?.();
              confirm?.();
            }}
          >
            Reset
          </Button>
          <Button
            size="small"
            className="common-button ok-button"
            type="primary"
            onClick={() => {
              applyFilters({
                ...appliedFilter,
                [dataIndex]: whereFilter?.[dataIndex],
              });
              confirm?.();
            }}
          >
            Ok
          </Button>
        </div>
      </div>
    ),
    filterIcon: () =>
      appliedFilter?.[dataIndex] !== undefined &&
      appliedFilter?.[dataIndex] !== null ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setWhereFilter(appliedFilter);
    },
  });

  const columns = useMemo(
    () => [
      {
        title: 'NAME',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        width: 220,
        align: 'left',
        render: (_, record) => record?.name || '-',
      },
      {
        title: 'TYPE',
        dataIndex: 'type',
        key: 'type',
        width: 200,
        align: 'left',
        ellipsis: true,
        render: (_, record) => getRuleTypeLabel(record?.type),
        ...filterPopup('type', TYPE_FILTER_OPTIONS),
      },
      {
        title: 'DETAILS',
        dataIndex: 'config',
        key: 'config',
        ellipsis: true,
        className: 'table-tooltip-cell',
        render: (_, record) => <ReservationRuleDetailsPopover rule={record} />,
      },
      {
        title: 'ATTACHED TOYS',
        dataIndex: 'attachmentCount',
        key: 'attachmentCount',
        width: 140,
        align: 'left',
        render: (_, record) => record?.attachmentCount ?? 0,
      },
      {
        title: 'STATUS',
        dataIndex: 'isActive',
        key: 'status',
        width: 120,
        align: 'left',
        render: (_, record) =>
          record?.isActive ? (
            <Tag color={STATUS?.ACTIVE?.color}>{STATUS?.ACTIVE?.label}</Tag>
          ) : (
            <Tag color={STATUS?.INACTIVE?.color}>{STATUS?.INACTIVE?.label}</Tag>
          ),
        ...filterPopup('status', STATUS_FILTER_OPTIONS),
      },
      {
        title: 'CREATED AT',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 160,
        align: 'left',
        ellipsis: true,
        render: (_, record) =>
          formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      },
      {
        title: 'ACTIONS',
        dataIndex: 'actions',
        key: 'actions',
        width: 140,
        align: 'left',
        // eslint-disable-next-line no-undef
        fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
        render: (_, record) => (
          <div className="action-button">
            {hasUpdatePermission && (
              <Tooltip title="Edit" placement="top" zIndex={4}>
                <EditIcon
                  className="mr-16 pointer svg-icon"
                  onClick={() => {
                    setCurrentRule(record);
                    setIsRuleModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
            {hasDeletePermission && (
              <Tooltip title="Delete" placement="top" zIndex={4}>
                <DeleteIcon
                  className="mr-16 pointer svg-icon"
                  onClick={() => {
                    setRuleToDelete(record);
                    setIsDeletePrompt(true);
                  }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      appliedFilter,
      hasDeletePermission,
      hasUpdatePermission,
      whereFilter,
    ],
  );

  const locale = {
    emptyText: isRuleLoading ? <span /> : 'No data found',
  };

  return (
    <>
      {(hasCreatePermission || hasUpdatePermission) && (
        <ConfigReservationRuleModal
          form={form}
          rule={currentRule}
          loadings={isActionLoading}
          isModalOpen={isRuleModalOpen}
          setIsModalOpen={setIsRuleModalOpen}
          onClose={() => setCurrentRule(null)}
          onFinish={handleSubmitRule}
        />
      )}

      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isDeletePrompt}
        onOk={handleDeleteRule}
        onCancel={() => {
          setIsDeletePrompt(false);
          setRuleToDelete(null);
        }}
        okButtonProps={{ loading: isActionLoading }}
      >
        Are you sure you want to delete{' '}
        <strong>{ruleToDelete?.name || 'this rule'}</strong>? This cannot be undone.
        {ruleToDelete?.attachmentCount > 0 && (
          <div className="mt-8">
            It is currently attached to {ruleToDelete?.attachmentCount} toy
            {ruleToDelete?.attachmentCount > 1 ? 's' : ''}.
          </div>
        )}
      </Modal>

      <div className="d-flex justify-end mb-16 flex-wrap">
        <SearchComponent
          className="search-box"
          query={ruleSearchTerm}
          setQuery={setRuleSearchTerm}
          getData={handleSearch}
          name="Rule"
        />
      </div>

      <CommonTable
        locale={locale}
        columns={columns}
        data={ruleList || []}
        loadingData={isRuleLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
      />
    </>
  );
};

export default ConfigReservationRuleTable;
